import brain from 'brain';
import { 
  sendOffer, 
  sendAnswer, 
  sendIceCandidate,
  sendEndedSignal,
  subscribeToAllSignals,
  registerParticipant,
  unregisterParticipant
} from './rtdbSignalingService';

// WebRTC configuration
const rtcConfiguration: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

// Callback type for when remote stream is received
type OnRemoteStreamCallback = (userId: string, stream: MediaStream) => void;

class WebRTCManager {
  private peerConnections: Map<string, RTCPeerConnection> = new Map();
  private localStream: MediaStream | null = null;
  private remoteStreams: Map<string, MediaStream> = new Map();
  private onRemoteStream: OnRemoteStreamCallback | null = null;
  private currentCallId: string | null = null;
  private currentUserId: string | null = null;
  private pendingIceCandidates: Map<string, RTCIceCandidateInit[]> = new Map();
  private pendingAnswers: Map<string, RTCSessionDescriptionInit> = new Map(); // Queue answers that arrive before peer connection
  private pendingOffers: Map<string, { callId: string; localUserId: string; offer: RTCSessionDescriptionInit }> = new Map(); // Queue offers
  private rtdbUnsubscribe: (() => void) | null = null; // RTDB subscription cleanup
  private useRTDB: boolean = true; // Flag to use RTDB signaling (can be toggled for fallback)

  setCurrentCallId(callId: string) {
    this.currentCallId = callId;
  }

  setCurrentUserId(userId: string) {
    this.currentUserId = userId;
  }

  setLocalStream(stream: MediaStream) {
    this.localStream = stream;
  }

  setOnRemoteStream(callback: OnRemoteStreamCallback) {
    this.onRemoteStream = callback;
  }

  /**
   * Initialize RTDB signaling subscriptions for a call
   */
  async initializeRTDBSignaling(callId: string, userId: string): Promise<void> {
    if (!this.useRTDB) return;
    
    console.log('[WebRTC] Initializing RTDB signaling for call:', callId, 'user:', userId);
    
    this.currentCallId = callId;
    this.currentUserId = userId;
    
    // Register participant presence
    await registerParticipant(callId, userId);
    
    // Subscribe to all RTDB signals
    this.rtdbUnsubscribe = subscribeToAllSignals(callId, userId, {
      onOffer: async (offer, fromUserId) => {
        console.log('[WebRTC] RTDB: Received offer from', fromUserId);
        await this.handleOffer(callId, userId, fromUserId, offer);
      },
      onAnswer: async (answer, fromUserId) => {
        console.log('[WebRTC] RTDB: Received answer from', fromUserId);
        await this.handleAnswer(fromUserId, answer);
      },
      onIceCandidate: async (candidate, fromUserId) => {
        console.log('[WebRTC] RTDB: Received ICE candidate from', fromUserId);
        await this.handleIceCandidate(fromUserId, candidate);
      },
      onCallEnded: (fromUserId) => {
        console.log('[WebRTC] RTDB: Call ended by', fromUserId);
        this.closeAll();
      },
      onParticipantJoined: (joinedUserId) => {
        console.log('[WebRTC] RTDB: Participant joined:', joinedUserId);
      },
      onParticipantLeft: (leftUserId) => {
        console.log('[WebRTC] RTDB: Participant left:', leftUserId);
        this.closePeerConnection(leftUserId);
      }
    });
    
    console.log('[WebRTC] RTDB signaling initialized');
  }

  // Create a peer connection for a specific remote user
  async createPeerConnection(
    callId: string,
    localUserId: string,
    remoteUserId: string,
    isInitiator: boolean
  ): Promise<RTCPeerConnection | null> {
    console.log(`[WebRTC] Creating peer connection for ${remoteUserId}, initiator: ${isInitiator}`);

    // Store call ID and user ID
    this.currentCallId = callId;
    this.currentUserId = localUserId;

    // Create new peer connection
    const pc = new RTCPeerConnection(rtcConfiguration);
    this.peerConnections.set(remoteUserId, pc);

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        console.log(`[WebRTC] Adding ${track.kind} track to peer connection for ${remoteUserId}`);
        console.log(`[WebRTC] Track state: enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`);
        pc.addTrack(track, this.localStream!);
      });
      
      // Brief delay for tracks to settle in the peer connection
      if (!isInitiator) {
        console.log(`[WebRTC] Brief delay for acceptor tracks to settle`);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Handle ICE candidates - send via RTDB
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log(`[WebRTC] Sending ICE candidate to ${remoteUserId} via RTDB`);
        try {
          if (this.useRTDB) {
            await sendIceCandidate(callId, localUserId, remoteUserId, event.candidate.toJSON());
          } else {
            // Fallback to API
            await brain.send_signal(
              { callId },
              {
                fromUserId: localUserId,
                toUserId: remoteUserId,
                type: 'ice-candidate',
                data: event.candidate.toJSON(),
              }
            );
          }
        } catch (error) {
          console.error('[WebRTC] Error sending ICE candidate:', error);
        }
      }
    };

    // Track ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log(`[WebRTC] ICE gathering state for ${remoteUserId}: ${pc.iceGatheringState}`);
    };

    // Track ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log(`[WebRTC] ICE connection state for ${remoteUserId}: ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'failed') {
        console.error(`[WebRTC] ICE connection failed for ${remoteUserId}`);
      }
    };

    // Handle incoming remote tracks
    pc.ontrack = (event) => {
      console.log('[WebRTC] Received remote track from', remoteUserId);
      console.log('[WebRTC] Track kind:', event.track.kind);
      console.log('[WebRTC] Track enabled:', event.track.enabled);
      console.log('[WebRTC] Track muted:', event.track.muted);
      console.log('[WebRTC] Track readyState:', event.track.readyState);
      console.log('[WebRTC] Number of streams:', event.streams.length);
      
      if (event.streams && event.streams[0]) {
        const stream = event.streams[0];
        console.log('[WebRTC] Stream id:', stream.id);
        console.log('[WebRTC] Stream audio tracks:', stream.getAudioTracks().length);
        console.log('[WebRTC] Stream video tracks:', stream.getVideoTracks().length);
        
        this.remoteStreams.set(remoteUserId, stream);
        if (this.onRemoteStream) {
          this.onRemoteStream(remoteUserId, stream);
        }
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = async () => {
      console.log(`[WebRTC] Connection state with ${remoteUserId}: ${pc.connectionState}`);
      
      // Update backend status when connection becomes connected
      // Use 'in_progress' to match native app status naming
      if (pc.connectionState === 'connected' && this.currentCallId) {
        console.log(`[WebRTC] Connection established with ${remoteUserId}, updating backend status to in_progress`);
        try {
          await brain.update_call_status(
            { callId: this.currentCallId },
            { status: 'in_progress' as any, additionalData: null }
          );
          console.log('[WebRTC] Backend status updated to in_progress');
        } catch (error) {
          console.error('[WebRTC] Error updating backend status to in_progress:', error);
        }
      }
      
      // Log failures and disconnections
      if (pc.connectionState === 'failed') {
        console.error(`[WebRTC] Connection with ${remoteUserId} failed`);
        if (this.currentCallId) {
          try {
            await brain.update_call_status(
              { callId: this.currentCallId },
              { status: 'failed', additionalData: null }
            );
          } catch (error) {
            console.error('[WebRTC] Error updating backend status to failed:', error);
          }
        }
      }
      
      if (pc.connectionState === 'disconnected') {
        console.log(`[WebRTC] Connection with ${remoteUserId} disconnected`);
      }
    };

    // Process any pending ICE candidates for this peer
    const pendingCandidates = this.pendingIceCandidates.get(remoteUserId) || [];
    if (pendingCandidates.length > 0) {
      console.log(`[WebRTC] Processing ${pendingCandidates.length} pending ICE candidates for ${remoteUserId}`);
      for (const candidate of pendingCandidates) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (error) {
          console.error('[WebRTC] Error adding pending ICE candidate:', error);
        }
      }
      this.pendingIceCandidates.delete(remoteUserId);
    }

    // If initiator, create and send offer via RTDB
    if (isInitiator) {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        console.log(`[WebRTC] Sending offer to ${remoteUserId} via RTDB`);
        
        if (this.useRTDB) {
          await sendOffer(callId, localUserId, remoteUserId, pc.localDescription!);
        } else {
          // Fallback to API
          await brain.send_signal(
            { callId },
            {
              fromUserId: localUserId,
              toUserId: remoteUserId,
              type: 'offer',
              data: pc.localDescription?.toJSON() || {},
            }
          );
        }
        
        // Check for pending answer that arrived before we sent the offer
        const pendingAnswer = this.pendingAnswers.get(remoteUserId);
        if (pendingAnswer) {
          console.log(`[WebRTC] Found pending answer from ${remoteUserId}, processing now`);
          this.pendingAnswers.delete(remoteUserId);
          // Small delay to ensure offer is fully processed
          setTimeout(() => {
            this.handleAnswer(remoteUserId, pendingAnswer);
          }, 100);
        }
      } catch (error) {
        console.error('[WebRTC] Error creating offer:', error);
      }
    } else {
      // If not initiator, check for pending offer
      const pendingOffer = this.pendingOffers.get(remoteUserId);
      if (pendingOffer) {
        console.log(`[WebRTC] Found pending offer from ${remoteUserId}, processing now`);
        this.pendingOffers.delete(remoteUserId);
        await this.handleOffer(pendingOffer.callId, pendingOffer.localUserId, remoteUserId, pendingOffer.offer);
      }
    }
    
    return pc;
  }

  // Handle incoming offer from remote peer
  async handleOffer(
    callId: string,
    localUserId: string,
    remoteUserId: string,
    offerData: any
  ): Promise<void> {
    console.log(`[WebRTC] Handling offer from ${remoteUserId}`);
    
    const pc = this.peerConnections.get(remoteUserId);
    if (!pc) {
      // Queue the offer for when peer connection is created
      console.log(`[WebRTC] No peer connection yet for ${remoteUserId}, queuing offer for later`);
      this.pendingOffers.set(remoteUserId, { callId, localUserId, offer: offerData });
      return;
    }

    // Check if already have a remote description (offer already processed)
    if (pc.remoteDescription) {
      console.log(`[WebRTC] Remote description already set for ${remoteUserId}, skipping duplicate offer`);
      return;
    }

    // Ensure peer connection is in stable state before processing offer
    if (pc.signalingState !== 'stable' && pc.signalingState !== 'have-local-offer') {
      console.warn(`[WebRTC] Peer connection not in stable state (${pc.signalingState}), queuing offer for retry`);
      this.pendingOffers.set(remoteUserId, { callId, localUserId, offer: offerData });
      setTimeout(() => {
        const pending = this.pendingOffers.get(remoteUserId);
        if (pending) {
          console.log(`[WebRTC] Retrying pending offer from ${remoteUserId}`);
          this.pendingOffers.delete(remoteUserId);
          this.handleOffer(pending.callId, pending.localUserId, remoteUserId, pending.offer);
        }
      }, 500);
      return;
    }

    try {
      console.log(`[WebRTC] Setting remote description (offer) for ${remoteUserId}`);
      await pc.setRemoteDescription(new RTCSessionDescription(offerData));
      console.log(`[WebRTC] ✅ Remote description (offer) set successfully for ${remoteUserId}`);
      
      // Process any pending ICE candidates now that remote description is set
      const pendingCandidates = this.pendingIceCandidates.get(remoteUserId) || [];
      if (pendingCandidates.length > 0) {
        console.log(`[WebRTC] Processing ${pendingCandidates.length} pending ICE candidates for ${remoteUserId} after setting remote description`);
        for (const candidate of pendingCandidates) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (error) {
            console.error('[WebRTC] Error adding pending ICE candidate:', error);
          }
        }
        this.pendingIceCandidates.delete(remoteUserId);
      }
      
      // Verify tracks are enabled and ready before creating answer
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          console.log(`[WebRTC] Track ${track.kind} state before answer: enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`);
        });
      }
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      console.log(`[WebRTC] Sending answer to ${remoteUserId} via RTDB`);
      
      if (this.useRTDB) {
        await sendAnswer(callId, localUserId, remoteUserId, pc.localDescription!);
      } else {
        // Fallback to API
        await brain.send_signal(
          { callId },
          {
            fromUserId: localUserId,
            toUserId: remoteUserId,
            type: 'answer',
            data: pc.localDescription?.toJSON() || {},
          }
        );
      }
    } catch (error) {
      console.error('[WebRTC] Error handling offer:', error);
    }
  }

  // Handle incoming answer from remote peer
  async handleAnswer(remoteUserId: string, answerData: any): Promise<void> {
    console.log(`[WebRTC] Handling answer from ${remoteUserId}`);
    
    const pc = this.peerConnections.get(remoteUserId);
    if (!pc) {
      // Queue the answer for when peer connection is created
      console.log(`[WebRTC] No peer connection yet for ${remoteUserId}, queuing answer for later`);
      this.pendingAnswers.set(remoteUserId, answerData);
      return;
    }

    // Check if already have a remote description (answer already processed)
    if (pc.remoteDescription) {
      console.log(`[WebRTC] Remote description already set for ${remoteUserId}, skipping duplicate answer`);
      return;
    }

    // Ensure we're expecting an answer (should be in have-local-offer state)
    if (pc.signalingState !== 'have-local-offer') {
      console.warn(`[WebRTC] Unexpected signaling state for answer: ${pc.signalingState}, queuing for retry`);
      this.pendingAnswers.set(remoteUserId, answerData);
      // Retry after a short delay
      setTimeout(() => {
        const pendingAnswer = this.pendingAnswers.get(remoteUserId);
        if (pendingAnswer) {
          console.log(`[WebRTC] Retrying pending answer for ${remoteUserId}`);
          this.pendingAnswers.delete(remoteUserId);
          this.handleAnswer(remoteUserId, pendingAnswer);
        }
      }, 500);
      return;
    }

    try {
      console.log(`[WebRTC] Setting remote description (answer) for ${remoteUserId}`);
      await pc.setRemoteDescription(new RTCSessionDescription(answerData));
      console.log(`[WebRTC] ✅ Remote description (answer) set successfully for ${remoteUserId}`);
      
      // Process any pending ICE candidates now that remote description is set
      const pendingCandidates = this.pendingIceCandidates.get(remoteUserId) || [];
      if (pendingCandidates.length > 0) {
        console.log(`[WebRTC] Processing ${pendingCandidates.length} pending ICE candidates for ${remoteUserId} after setting remote description`);
        for (const candidate of pendingCandidates) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (error) {
            console.error('[WebRTC] Error adding pending ICE candidate:', error);
          }
        }
        this.pendingIceCandidates.delete(remoteUserId);
      }
    } catch (error) {
      console.error('[WebRTC] Error handling answer:', error);
    }
  }

  // Handle incoming ICE candidate from remote peer
  async handleIceCandidate(remoteUserId: string, candidateData: any): Promise<void> {
    console.log(`[WebRTC] Handling ICE candidate from ${remoteUserId}`);
    
    const pc = this.peerConnections.get(remoteUserId);
    if (!pc) {
      console.warn(`[WebRTC] No peer connection found for ${remoteUserId}, queuing ICE candidate`);
      const queue = this.pendingIceCandidates.get(remoteUserId) || [];
      queue.push(candidateData);
      this.pendingIceCandidates.set(remoteUserId, queue);
      return;
    }

    // Only add ICE candidates after remote description is set
    if (!pc.remoteDescription) {
      console.warn(`[WebRTC] Remote description not set for ${remoteUserId}, queuing ICE candidate`);
      const queue = this.pendingIceCandidates.get(remoteUserId) || [];
      queue.push(candidateData);
      this.pendingIceCandidates.set(remoteUserId, queue);
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidateData));
      console.log(`[WebRTC] Successfully added ICE candidate from ${remoteUserId}`);
    } catch (error) {
      console.error('[WebRTC] Error adding ICE candidate:', error);
    }
  }

  // Close a specific peer connection
  closePeerConnection(remoteUserId: string): void {
    const pc = this.peerConnections.get(remoteUserId);
    if (pc) {
      pc.close();
      this.peerConnections.delete(remoteUserId);
      console.log(`[WebRTC] Closed peer connection with ${remoteUserId}`);
    }
  }

  // Replace video track in all peer connections
  replaceVideoTrack(newVideoTrack: MediaStreamTrack): void {
    console.log('[WebRTC] Replacing video track in all peer connections');
    this.peerConnections.forEach((pc, userId) => {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        sender.replaceTrack(newVideoTrack).then(() => {
          console.log(`[WebRTC] Replaced video track for ${userId}`);
        }).catch((error) => {
          console.error(`[WebRTC] Error replacing video track for ${userId}:`, error);
        });
      }
    });
  }

  // Send call ended signal via RTDB and close all connections
  async endCall(): Promise<void> {
    if (this.currentCallId && this.currentUserId && this.useRTDB) {
      console.log('[WebRTC] Sending end call signals via RTDB');
      
      // Send ended signal to all other participants
      for (const [remoteUserId] of this.peerConnections) {
        try {
          await sendEndedSignal(this.currentCallId, this.currentUserId, remoteUserId);
        } catch (error) {
          console.error(`[WebRTC] Error sending ended signal to ${remoteUserId}:`, error);
        }
      }
      
      // Unregister participant
      try {
        await unregisterParticipant(this.currentCallId, this.currentUserId);
      } catch (error) {
        console.error('[WebRTC] Error unregistering participant:', error);
      }
    }
    
    this.closeAll();
  }

  // Close all peer connections and clean up
  closeAll(): void {
    console.log('[WebRTC] Closing all peer connections');
    
    // Cleanup RTDB subscriptions
    if (this.rtdbUnsubscribe) {
      this.rtdbUnsubscribe();
      this.rtdbUnsubscribe = null;
      console.log('[WebRTC] RTDB subscriptions cleaned up');
    }
    
    // Close all peer connections
    this.peerConnections.forEach((pc, userId) => {
      console.log(`[WebRTC] Closing connection with ${userId}`);
      pc.close();
    });
    this.peerConnections.clear();
    
    // Clear all pending queues
    this.pendingIceCandidates.clear();
    this.pendingAnswers.clear();
    this.pendingOffers.clear();

    // Stop local stream tracks
    if (this.localStream) {
      console.log('[WebRTC] Stopping local stream tracks');
      this.localStream.getTracks().forEach(track => {
        console.log(`[WebRTC] Stopping local ${track.kind} track`);
        track.stop();
      });
      this.localStream = null;
    }
    
    // Stop and clear remote streams
    this.remoteStreams.forEach((stream, userId) => {
      console.log(`[WebRTC] Stopping remote stream from ${userId}`);
      stream.getTracks().forEach(track => {
        console.log(`[WebRTC] Stopping remote ${track.kind} track from ${userId}`);
        track.stop();
      });
    });
    this.remoteStreams.clear();
    
    // Clear call context
    this.currentCallId = null;
    this.currentUserId = null;
    
    console.log('[WebRTC] All connections and streams closed');
  }
}

// Export singleton instance
export const webRTCManager = new WebRTCManager();
